import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import InputField from '../../pages/Forms/InputField';
import ConfirmationPopup from '../../pages/Forms/Confirm';
import SendingStatusPopup from '../../pages/Forms/SendingStatusPopup';
import API_ENDPOINTS from '../../api';

const AddOpinion = () => {
  const { opinionId } = useParams();
  const navigate = useNavigate();

  const [opinion, setOpinion] = useState({
    text: '',
    rating: 5,
    language: 'ar',
    isNew: false,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [sendingStatus, setSendingStatus] = useState('');
  const [error, setError] = useState('');

  // ================= Fetch opinion (Edit) =================
  useEffect(() => {
    if (opinionId) {
      setIsEditing(true);
      const fetchOpinion = async () => {
        try {
          const { data } = await axios.get(
            API_ENDPOINTS.OPINIONS.GET_ONE(opinionId)
          );
          setOpinion(data.opinion);
        } catch {
          setError('Failed to load opinion');
        }
      };
      fetchOpinion();
    }
  }, [opinionId]);

  // ================= Handle Change =================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setOpinion((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // ================= Submit =================
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setIsConfirmOpen(false);
    setSendingStatus('sending');

    try {
      if (isEditing) {
        await axios.patch(
          API_ENDPOINTS.OPINIONS.UPDATE(opinionId),
          opinion
        );
      } else {
        await axios.post(
          API_ENDPOINTS.OPINIONS.ADD,
          opinion
        );
      }

      setSendingStatus('success');
      navigate('/admin/opinions');
    } catch (err) {
      console.error(err);
      setSendingStatus('failure');
    }
  };

  return (
    <div className="p-6 bg-white rounded-md shadow-md">
      <h3 className="text-2xl font-extrabold text-primeColor mb-4 border-b-2 border-primeColor pb-2">
        {isEditing ? 'Edit Opinion' : 'Add New Opinion'}
      </h3>

      {error && <p className="text-red-500 mb-3">{error}</p>}

      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Opinion Text */}
        <div>
          <label className="block mb-1 font-semibold">Opinion Text</label>
          <textarea
            name="text"
            value={opinion.text}
            onChange={handleChange}
            required
            rows={4}
            className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primeColor"
          />
        </div>

        {/* Rating */}
        <InputField
          label="Rating (1 - 5)"
          type="number"
          name="rating"
          min="1"
          max="5"
          value={opinion.rating}
          onChange={handleChange}
        />

        {/* Language */}
        <div>
          <label className="block mb-1 font-semibold">Language</label>
          <select
            name="language"
            value={opinion.language}
            onChange={handleChange}
            className="w-full p-3 border rounded-lg"
          >
            <option value="ar">Arabic</option>
            <option value="en">English</option>
          </select>
        </div>

        {/* Is New */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            name="isNew"
            checked={opinion.isNew}
            onChange={handleChange}
          />
          <span className="font-medium">Mark as New</span>
        </div>

        <button
          type="submit"
          className="px-6 py-2 font-bold text-white bg-primeColor-500 rounded-lg hover:opacity-90"
        >
          {sendingStatus === 'sending'
            ? 'Loading...'
            : isEditing
            ? 'Update Opinion'
            : 'Add Opinion'}
        </button>
      </form>

      {/* Confirmation */}
      {isConfirmOpen && (
        <ConfirmationPopup
          onConfirm={handleConfirmSubmit}
          onCancel={() => setIsConfirmOpen(false)}
        />
      )}

      {/* Status */}
      {sendingStatus && (
        <SendingStatusPopup
          status={sendingStatus}
          onClose={() => setSendingStatus('')}
        />
      )}
    </div>
  );
};

export default AddOpinion;
